import { useCallback, useState } from 'react';
import { ComparisonOperator, LogicalOperator } from 'types/dataHelios';

import type { FilterCondition } from 'types/tableFilters';
import type { FilterFormValues } from 'utils/helios/filterFormUtils';

const { Equals } = ComparisonOperator;
const { And } = LogicalOperator;

const createInitialFormValues = (): FilterFormValues => ({
  columnKey: '',
  operator: Equals,
  value: '',
  logicalOperator: And,
});

const toStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter((item): item is string => typeof item === 'string');
};

const getNextBlankState = (value: boolean | null): boolean | null => (value === null ? true : value === true ? false : null);

interface UseFilterFormStateResult {
  formValues: FilterFormValues;
  isBlank: boolean | null;
  fromValue: string;
  toValue: string;
  staticValues: string[];
  currentChipText: string;
  advancedMode: boolean;
  setColumnKey: (columnKey: string) => void;
  setOperator: (operator: ComparisonOperator) => void;
  setValue: (value: string) => void;
  setLogicalOperator: (logicalOperator: LogicalOperator) => void;
  setFromValue: (value: string) => void;
  setToValue: (value: string) => void;
  setCurrentChipText: (value: string) => void;
  setAdvancedMode: (value: boolean) => void;
  setStaticValues: (values: string[]) => void;
  setStaticValuesFromUnknown: (value: unknown) => void;
  toggleIsBlank: () => void;
  addChip: () => void;
  removeChip: (index: number) => void;
  resetForm: () => void;
  loadFilterToForm: (filter: FilterCondition) => void;
}

export const useFilterFormState = (): UseFilterFormStateResult => {
  const [formValues, setFormValues] = useState<FilterFormValues>(createInitialFormValues);
  const [isBlank, setIsBlank] = useState<boolean | null>(null);
  const [fromValue, setFromValue] = useState('');
  const [toValue, setToValue] = useState('');
  const [staticValues, setStaticValues] = useState<string[]>([]);
  const [currentChipText, setCurrentChipText] = useState('');
  const [advancedMode, setAdvancedMode] = useState(false);

  const resetForm = useCallback(() => {
    setFormValues(createInitialFormValues());
    setIsBlank(null);
    setFromValue('');
    setToValue('');
    setStaticValues([]);
    setCurrentChipText('');
    setAdvancedMode(false);
  }, []);

  const setColumnKey = useCallback((columnKey: string) => {
    setFormValues((prevValues) => ({
      ...prevValues,
      columnKey,
      operator: Equals,
      value: '',
    }));
    setIsBlank(null);
    setFromValue('');
    setToValue('');
    setStaticValues([]);
    setCurrentChipText('');
  }, []);

  const setOperator = useCallback((operator: ComparisonOperator) => {
    setFormValues((prevValues) => ({ ...prevValues, operator }));
  }, []);

  const setValue = useCallback((value: string) => {
    setFormValues((prevValues) => ({ ...prevValues, value }));
  }, []);

  const setLogicalOperator = useCallback((logicalOperator: LogicalOperator) => {
    setFormValues((prevValues) => ({ ...prevValues, logicalOperator }));
  }, []);

  const setStaticValuesFromUnknown = useCallback((value: unknown) => {
    setStaticValues(toStringArray(value));
  }, []);

  const clearValueFields = useCallback(() => {
    setFromValue('');
    setToValue('');
    setStaticValues([]);
    setCurrentChipText('');
    setValue('');
  }, [setValue]);

  const toggleIsBlank = useCallback(() => {
    setIsBlank((prev) => {
      if (prev === null) {
        clearValueFields();
      }

      return getNextBlankState(prev);
    });
  }, [clearValueFields]);

  const addChip = useCallback(() => {
    const trimmed = currentChipText.trim();
    if (!trimmed) {
      return;
    }

    setStaticValues((prevValues) => [...prevValues, trimmed]);
    setCurrentChipText('');
  }, [currentChipText]);

  const removeChip = useCallback((index: number) => {
    setStaticValues((prevValues) => prevValues.filter((_, currentIndex) => currentIndex !== index));
  }, []);

  const loadFilterToForm = useCallback((filter: FilterCondition) => {
    setFormValues({
      columnKey: filter.columnKey,
      operator: filter.operator,
      value: filter.value,
      logicalOperator: filter.logicalOperator || And,
    });
    setIsBlank(filter.isBlank ?? null);
    setFromValue(filter.fromValue || '');
    setToValue(filter.toValue || '');
    setStaticValues(filter.staticValues || []);
    setCurrentChipText('');
  }, []);

  return {
    formValues,
    isBlank,
    fromValue,
    toValue,
    staticValues,
    currentChipText,
    advancedMode,
    setColumnKey,
    setOperator,
    setValue,
    setLogicalOperator,
    setFromValue,
    setToValue,
    setCurrentChipText,
    setAdvancedMode,
    setStaticValues,
    setStaticValuesFromUnknown,
    toggleIsBlank,
    addChip,
    removeChip,
    resetForm,
    loadFilterToForm,
  };
};
